import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { useAuth } from '@/api/auth'
import styles from './LinkPreviews.module.css'

export interface LinkPreview {
  url: string
  title: string
  description: string | null
  hostname: string
  thumbnail_path: string | null
}

function destination(raw: string): string | null {
  try {
    const url = new URL(raw)
    return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password ? url.href : null
  } catch { return null }
}

function Thumbnail({ path }: { path: string }) {
  const [source, setSource] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    const controller = new AbortController()
    let objectUrl: string | null = null
    setSource(null)
    setFailed(false)
    void api.image(path, controller.signal).then(blob => {
      if (controller.signal.aborted || !['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(blob.type.split(';')[0] ?? '')) return
      objectUrl = URL.createObjectURL(blob)
      setSource(objectUrl)
    }).catch(() => { if (!controller.signal.aborted) setFailed(true) })
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl) }
  }, [path])
  if (failed) return null
  return <span className={styles.thumbnail} aria-hidden="true">
    {source ? <img src={source} alt="" onError={() => setFailed(true)} /> : null}
  </span>
}

export function LinkPreviewCard({ preview, messageId }: { preview: LinkPreview; messageId: string }) {
  const href = destination(preview.url)
  if (!href) return null
  const prefix = `/messages/${encodeURIComponent(messageId)}/link-previews/`
  const thumbnail = preview.thumbnail_path?.startsWith(prefix) && /^[-\w]+\/image$/.test(preview.thumbnail_path.slice(prefix.length))
    ? preview.thumbnail_path : null
  return <a className={styles.card} href={href} target="_blank" rel="noopener noreferrer nofollow">
    {thumbnail ? <Thumbnail path={thumbnail} /> : null}
    <span className={styles.details}>
      <span className={styles.hostname}>{new URL(href).hostname}</span>
      <strong className={styles.title}>{preview.title}</strong>
      {preview.description ? <span className={styles.description}>{preview.description}</span> : null}
    </span>
  </a>
}

interface LinkPreviewPage {
  previews: LinkPreview[]
  /** The Space is still fetching at least one link. Older Spaces omit it. */
  pending?: boolean
  retry_after_ms?: number
}

/** Enough re-asks to cover a slow site, few enough to leave the room alone. */
const MAX_PENDING_POLLS = 4
const DEFAULT_RETRY_MS = 3_000

export function LinkPreviews({ messageId, text }: { messageId: string; text: string }) {
  const { user } = useAuth()
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)
  const [polls, setPolls] = useState(0)
  const hasLink = /https?:\/\/|www\./i.test(text)
  useEffect(() => {
    if (!hasLink || visible) return
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { setVisible(true); observer.disconnect() }
    }, { rootMargin: '200px 0px' })
    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [hasLink, visible])
  useEffect(() => { setPolls(0) }, [messageId, text])
  const query = useQuery({
    queryKey: ['link-previews', api.getBasePath(), user?.id, messageId, text],
    queryFn: ({ signal }) => api.get<LinkPreviewPage>(`/messages/${encodeURIComponent(messageId)}/link-previews`, undefined, signal),
    enabled: hasLink && visible,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    // A cold link is fetched from the site itself, so the first answer often
    // carries no card yet. Ask again while the Space says it is still working,
    // backing off each time, or the message keeps its bare link forever.
    retry: 1,
    refetchOnWindowFocus: false,
    refetchInterval: ({ state }) => {
      if (state.data?.pending !== true || polls >= MAX_PENDING_POLLS) return false
      return Math.min((state.data.retry_after_ms ?? DEFAULT_RETRY_MS) * 2 ** polls, 30_000)
    },
  })
  const pending = query.data?.pending === true
  const updatedAt = query.dataUpdatedAt
  useEffect(() => {
    if (updatedAt > 0 && pending) setPolls(count => count + 1)
  }, [updatedAt, pending])
  if (!hasLink) return null
  return <div ref={ref} className={styles.list}>
    {query.data?.previews.slice(0, 3).map(preview => <LinkPreviewCard key={preview.url} preview={preview} messageId={messageId} />)}
  </div>
}
